import React, { useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import isBefore from 'date-fns/isBefore';
import { useForm, Controller } from 'react-hook-form';
import BEM from '../../bem';
import tzTime from '../../utils/tzTime';
import mobxInjectSelect from '../../utils/mobxInjectSelect';
import { taskActionEvent } from '../../utils/taskTracking';
import type { LogGainsightAnalytics } from '../../utils/taskTracking';
import { toDueAtUtc, isFutureDateTime, toDatetimeLocal } from '../../utils/taskPrefill';
import { ReactComponent as CircledCheckMarckIcon } from '../../../common/images/circled-checkmark.svg';
import { ReactComponent as BellIcon } from '../../../common/images/bell.svg';
import { ReactComponent as CalendarIcon } from '../../../common/images/calendar.svg';
import { useSourceMessage } from '../../hooks/useSourceMessage';
import type { FullSourceMessage } from '../../../types/Tasks/sourceMessage';
import { ReactComponent as PencilIcon } from '../../../common/images/pencil.svg';
import PrioritySelect from '../PrioritySelect/PrioritySelect';
import { Switch } from '../WebComponents';
import SourceMessageCard from './SourceMessageCard';
import { useAppDispatch, useAppSelector, actions } from 'redux-stores';
import {
  useCompleteTaskMutation,
  useReopenTaskMutation,
  useUpdateTaskMutation,
} from 'redux-stores/tasks';
import { PRIORITY_LABEL } from 'js-sdk/src/types/Tasks';
import type { TaskPriority } from 'js-sdk/src/types/Tasks';

const classes = BEM.with('TaskView');

const MAX_TITLE_CHARACTERS = 255;

type MobxProps = {
  currentUserId: string;
  logGainsightAnalytics: LogGainsightAnalytics;
  findMessage: (id: string) => Promise<FullSourceMessage | undefined>;
  navigateToConversation: (
    entityType: string,
    id: string,
    orgId: string,
    sortNumber: number
  ) => Promise<void>;
  setFilterBar: (filter: string) => void;
};

type EditFormValues = {
  title: string;
  description: string;
  dueAt: string;
  reminderEnabled: boolean;
  priority: TaskPriority;
};

function TaskView({
  currentUserId,
  findMessage,
  logGainsightAnalytics,
  navigateToConversation,
  setFilterBar,
}: MobxProps) {
  const dispatch = useAppDispatch();
  const mountedRef = useRef(true);
  const isNavigatingRef = useRef(false);
  const selectedTask = useAppSelector((state) => state.tasks.selectedTask);
  const [completeTask, { isLoading: isCompleting }] = useCompleteTaskMutation();
  const [reopenTask, { isLoading: isReopening }] = useReopenTaskMutation();
  const [updateTask, { isLoading: isSaving }] = useUpdateTaskMutation();
  const organizationId = useAppSelector((state) => state.session.currentOrganizationId);
  const accessibilityMode = useAppSelector((state) => state.ui.accessibilityMode);
  const [isEditing, setIsEditing] = useState(false);
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, dirtyFields },
  } = useForm<EditFormValues>({
    defaultValues: {
      title: '',
      description: '',
      dueAt: '',
      reminderEnabled: false,
      priority: 'medium',
    },
  });

  const {
    message: sourceMessage,
    isUnavailable,
    isLoading: isMessageLoading,
  } = useSourceMessage(selectedTask, findMessage);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    setIsEditing(false);
  }, [selectedTask?.id]);

  if (!selectedTask || !organizationId) {
    return (
      <div className={classes('')} data-test-id="task-view">
        <div className={classes('empty')} data-test-id="task-view-empty">
          <CircledCheckMarckIcon /> Select a task to view details
        </div>
      </div>
    );
  }

  const isCompleted = selectedTask.status === 'completed';
  const isBusy = isCompleting || isReopening || isSaving;
  const isOverdue =
    !isCompleted && selectedTask.due_at
      ? isBefore(new Date(selectedTask.due_at), new Date())
      : false;
  const dueLabel = selectedTask.due_at
    ? tzTime(selectedTask.due_at).format('MMM D, YYYY h:mm A')
    : null;
  const completedAtLabel = selectedTask.completed_at
    ? tzTime(selectedTask.completed_at).format('MMM D')
    : null;
  const handleToggleComplete = async () => {
    if (isBusy) return;
    try {
      const task = isCompleted
        ? await reopenTask({ organizationId, taskId: selectedTask.id }).unwrap()
        : await completeTask({ organizationId, taskId: selectedTask.id }).unwrap();
      logGainsightAnalytics(
        taskActionEvent(
          isCompleted ? 'Reopen' : 'Complete',
          organizationId,
          currentUserId,
          selectedTask.id
        )
      );
      dispatch(actions.updateSelectedTask(task));
    } catch {
      console.error('Failed to toggle task completion');
    }
  };

  const handleClose = () => {
    dispatch(actions.setSelectedTask(null));
  };

  const handleGoToMessage = async () => {
    // Guards against a second click landing mid-flight: without it, the second call's own
    // setSelectedMessageId('') / setIsGoToMessageNavigation(true) races the first call's
    // ConversationMessages cleanup timeout, which can clear the flags the second call just set.
    if (isNavigatingRef.current) return;
    const messageId = selectedTask?.source_ref?.message_id;
    if (!messageId) return;

    isNavigatingRef.current = true;
    try {
      await goToMessage(messageId);
    } finally {
      isNavigatingRef.current = false;
    }
  };

  const goToMessage = async (messageId: string) => {
    const fullMsg = await findMessage(messageId);
    if (!mountedRef.current) return;
    if (fullMsg?.sortNumber == null || !fullMsg?.counterPartyType || !fullMsg?.counterPartyId) {
      Sentry.logger.warn(
        Sentry.logger.fmt`handleGoToMessage: missing fields on message ${messageId}`
      );
      return;
    }
    setFilterBar(fullMsg.counterParty?.groupType === 'PATIENT_CARE' ? 'Right' : 'Left');

    // isGoToMessageNavigation must be true *before* navigateToConversation resolves: it flips
    // isLoadingConversation back to false internally, and ConversationMessages guards its
    // forced-scroll-to-bottom-on-load-complete behavior on this flag. Setting it only after the
    // await left a gap where that guard wasn't active yet, so the bottom-scroll fired first and
    // won the race against the go-to-message scroll.
    //
    // selectedMessageId / selectedHighlightedMessageId still must wait until after
    // navigateToConversation resolves: same order as keyword search (KeywordMessageBubble) —
    // navigateToConversation synchronously sets isLoadingConversation + the sortNumber anchor
    // before its own async fetch, and setting selectedMessageId before that resolves lets
    // ConversationMessages react while isLoadingConversation is still false and the old
    // conversation is mounted, triggering a wrong-order missing-message fetch.
    // Clear selectedMessageId first: clicking the same source message twice in a row would
    // otherwise dispatch the same id it already holds (e.g. the previous run's own delayed
    // clear hadn't flushed yet) — react-redux treats that as a no-op, ConversationMessages never
    // re-renders, and the go-to-message scroll silently never re-runs on the second click.
    dispatch(actions.setSelectedMessageId(''));
    dispatch(actions.setIsGoToMessageNavigation(true));
    try {
      await navigateToConversation(
        fullMsg.counterPartyType,
        fullMsg.counterPartyId,
        organizationId,
        fullMsg.sortNumber
      );
    } catch (err) {
      dispatch(actions.setIsGoToMessageNavigation(false));
      throw err;
    }
    const highlightId = fullMsg.serverId ?? messageId;
    dispatch(actions.setSelectedMessageId(highlightId));
    dispatch(actions.setSelectedHighlightedMessageId(highlightId));
    dispatch(actions.setGotoMessageNavigationEvent(true));
  };

  const startEdit = () => {
    reset({
      title: selectedTask.title,
      description: selectedTask.description ?? '',
      dueAt: toDatetimeLocal(selectedTask.due_at),
      reminderEnabled: selectedTask.reminder_enabled,
      priority: selectedTask.priority ?? 'medium',
    });
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
  };

  const onSaveEdit = async (values: EditFormValues) => {
    try {
      const task = await updateTask({
        organizationId,
        taskId: selectedTask.id,
        payload: {
          title: values.title.trim(),
          description: values.description || null,
          due_at: toDueAtUtc(values.dueAt),
          reminder_enabled: values.reminderEnabled,
          priority: values.priority,
        },
      }).unwrap();
      logGainsightAnalytics(
        taskActionEvent('Update', organizationId, currentUserId, selectedTask.id)
      );
      dispatch(actions.updateSelectedTask(task));
      setIsEditing(false);
    } catch {
      console.error('Failed to update task');
    }
  };

  return (
    <div className={classes('')} data-test-id="task-view">
      <div className={classes('header')}>
        <div className={classes('header--content')}>
          <div className={classes('header--pills')}>
            {isEditing ? (
              <div className={classes('priority', { editing: isEditing })}>
                <Controller
                  control={control}
                  name="priority"
                  render={({ field }) => (
                    <PrioritySelect
                      borderless
                      value={field.value}
                      onChange={field.onChange}
                      accessibilityMode={accessibilityMode}
                      buttonTestId="task-view-priority-btn"
                    />
                  )}
                />
              </div>
            ) : (
              selectedTask.priority && (
                <div className={classes('priority')}>
                  <span className={classes('priority-pill', { priority: selectedTask.priority })}>
                    {PRIORITY_LABEL[selectedTask.priority]}
                  </span>
                </div>
              )
            )}
            <div className={classes('status')}>
              {selectedTask.status === 'open' ? (
                <span className={classes('status-pill', { status: selectedTask.status })}>
                  Open
                </span>
              ) : (
                <span className={classes('status-pill', { status: selectedTask.status })}>
                  Completed
                </span>
              )}
            </div>
          </div>
          {isEditing ? (
            <>
              <Controller
                control={control}
                name="title"
                rules={{
                  required: true,
                  validate: (v) => v.trim().length > 0 || 'Title is required.',
                  maxLength: MAX_TITLE_CHARACTERS,
                }}
                render={({ field }) => (
                  <input
                    {...field}
                    className={classes('title-input')}
                    placeholder="Task title"
                    aria-describedby={errors.title ? 'task-view-title-error' : undefined}
                    aria-invalid={!!errors.title}
                    data-test-id="task-view-title-input"
                  />
                )}
              />
              {errors.title && (
                <div className={classes('field-error')} id="task-view-title-error" role="alert">
                  {errors.title.message || 'Title is required.'}
                </div>
              )}
            </>
          ) : (
            <h2 className={classes('title')}>{selectedTask.title}</h2>
          )}
        </div>
        {isEditing ? (
          <div className={classes('edit-actions')}>
            <button
              className={classes('cancel-btn')}
              onClick={cancelEdit}
              disabled={isSaving}
              type="button"
              data-test-id="task-view-cancel-edit-btn"
            >
              Cancel
            </button>
            <button
              className={classes('save-btn')}
              onClick={handleSubmit(onSaveEdit)}
              disabled={isSaving}
              type="button"
              data-test-id="task-view-save-edit-btn"
            >
              Save
            </button>
          </div>
        ) : !isCompleted ? (
          <button
            className={classes('edit-btn')}
            onClick={startEdit}
            type="button"
            aria-label="Edit task"
            data-test-id="task-view-edit-btn"
          >
            <PencilIcon className={classes('edit-icon')} />
            Edit
          </button>
        ) : null}
        <button
          className={classes('close-btn')}
          onClick={handleClose}
          type="button"
          aria-label="Close task view"
          data-test-id="task-view-close"
        >
          ✕
        </button>
      </div>

      <div className={classes('actions')}>
        <button
          className={classes('complete-btn', { completed: isCompleted })}
          onClick={handleToggleComplete}
          disabled={isBusy || isEditing}
          type="button"
          data-test-id="task-view-complete-btn"
        >
          {isCompleted ? (
            <>
              <CircledCheckMarckIcon />
              Completed
            </>
          ) : (
            <>
              <CircledCheckMarckIcon />
              Mark Complete
            </>
          )}
        </button>
      </div>

      <div className={classes('section')}>
        <div className={classes('section-columns')}>
          <div>
            <h3 className={classes('section-title')}>Due Date</h3>
            {isEditing ? (
              <>
                <Controller
                  control={control}
                  name="dueAt"
                  rules={{
                    validate: (v) =>
                      !dirtyFields.dueAt ||
                      v === '' ||
                      isFutureDateTime(v) ||
                      'Due date & time must be in the future.',
                  }}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="datetime-local"
                      min={toDatetimeLocal(new Date().toISOString())}
                      aria-describedby={errors.dueAt ? 'task-view-due-date-error' : undefined}
                      aria-invalid={!!errors.dueAt}
                      className={classes('edit-input', { error: !!errors.dueAt })}
                      data-test-id="task-view-due-date-input"
                    />
                  )}
                />
                {errors.dueAt && (
                  <div
                    className={classes('field-error')}
                    id="task-view-due-date-error"
                    role="alert"
                  >
                    {errors.dueAt.message}
                  </div>
                )}
              </>
            ) : (
              dueLabel && (
                <span className={classes('due-pill', { overdue: isOverdue })}>
                  <CalendarIcon />
                  {isOverdue ? 'Overdue • ' : null}
                  {dueLabel}
                </span>
              )
            )}
          </div>
          <div>
            <h3 className={classes('section-title')}>Reminder</h3>
            {isEditing ? (
              <Controller
                control={control}
                name="reminderEnabled"
                render={({ field }) => (
                  <Switch
                    checked={field.value}
                    onSwitch={() => field.onChange(!field.value)}
                    dataTestId="task-view-reminder-toggle"
                  />
                )}
              />
            ) : (
              <div className={classes('section--container')}>
                <BellIcon />
                {selectedTask.reminder_enabled ? 'On' : 'Off'}
              </div>
            )}
          </div>
        </div>
      </div>

      {(isEditing || selectedTask.description) && (
        <div className={classes('section')}>
          <h3 className={classes('section-title')}>Description</h3>
          {isEditing ? (
            <Controller
              control={control}
              name="description"
              render={({ field }) => (
                <textarea
                  {...field}
                  className={classes('edit-textarea')}
                  placeholder="Add a description"
                  data-test-id="task-view-description-input"
                />
              )}
            />
          ) : (
            <p className={classes('card')}>{selectedTask.description}</p>
          )}
        </div>
      )}

      {selectedTask.source_type === 'message' && (
        <div className={classes('section')} data-test-id="task-view-source-message-section">
          <h3 className={classes('section-title')}>Source Message</h3>
          {isMessageLoading ? null : sourceMessage ? (
            <SourceMessageCard message={sourceMessage} onClick={handleGoToMessage} />
          ) : isUnavailable ? (
            <p className={classes('card')} data-test-id="task-view-message-unavailable">
              Message no longer available
            </p>
          ) : null}
        </div>
      )}

      {selectedTask.completed_at && (
        <div className={classes('section')}>
          <h3 className={classes('section-title')}>Completed on</h3>
          <p className={classes('card')}>
            <CircledCheckMarckIcon />
            {completedAtLabel}
          </p>
        </div>
      )}
    </div>
  );
}

export default mobxInjectSelect<Record<string, object>, MobxProps>({
  messageStore: ['findMessage'],
  conversationStore: ['navigateToConversation'],
  rosterStore: ['setFilterBar'],
  sessionStore: ['currentUserId'],
  trackerStore: ['logGainsightAnalytics'],
})(TaskView);
